import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentUserId } from '@/lib/currentUser';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, FileSpreadsheet, Check, X, AlertCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { validateSiret, validatePostalCode } from '@/lib/validators';

interface ParsedSCI {
  row: number;
  name: string;
  siret: string;
  vat_number: string;
  address: string;
  postal_code: string;
  city: string;
  iban: string;
  bic: string;
  receipt_prefix: string;
  signature_name: string;
  email_reply_to: string;
  errors: string[];
  valid: boolean;
}

const TEMPLATE_HEADERS = [
  'Nom *',
  'SIRET (14 chiffres)',
  'N° TVA',
  'Adresse',
  'Code postal',
  'Ville',
  'IBAN',
  'BIC',
  'Préfixe quittance',
  'Nom signataire',
  'Email réponse',
];

export function ImportSCIsDialog() {
  const [open, setOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [parsedData, setParsedData] = useState<ParsedSCI[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data, error } = await supabase.from('clients').select('id, name');
      if (error) throw error;
      return data;
    },
  });

  const downloadTemplate = () => {
    const csvContent = [
      TEMPLATE_HEADERS.join(','),
      'SCI Exemple,12345678901234,FR12345678901,12 rue de la Paix,75001,Paris,FR7630001007941234567890185,BNPAFRPP,QUI,Jean Dupont,contact@sci-exemple.fr',
      'SCI Résidence Bleue,,,,69001,Lyon,,,,,',
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template_scis.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text: string): ParsedSCI[] => {
    const cleanText = text.replace(/^\ufeff/, '');
    const lines = cleanText.split(/\r?\n/).filter(line => line.trim());
    if (lines.length < 2) return [];

    const dataLines = lines.slice(1);

    // Auto-detect delimiter from header line
    const headerLine = lines[0];
    const delimiter = (headerLine.split(';').length > headerLine.split(',').length) ? ';' : ',';

    return dataLines.map((line, index) => {
      const values: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === delimiter && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const errors: string[] = [];
      const name = values[0] || '';
      const siret = values[1] || '';
      const vatNumber = values[2] || '';
      const address = values[3] || '';
      const postalCode = values[4] || '';
      const city = values[5] || '';
      const iban = values[6] || '';
      const bic = values[7] || '';
      const receiptPrefix = values[8] || '';
      const signatureName = values[9] || '';
      const emailReplyTo = values[10] || '';

      if (!name) {
        errors.push('Nom requis');
      }
      if (siret) {
        const siretValidation = validateSiret(siret);
        if (!siretValidation.valid) {
          errors.push(siretValidation.message || 'SIRET invalide');
        }
      }
      if (postalCode) {
        const postalValidation = validatePostalCode(postalCode);
        if (!postalValidation.valid) {
          errors.push(postalValidation.message || 'Code postal invalide');
        }
      }
      if (emailReplyTo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailReplyTo)) {
        errors.push('Email invalide');
      }

      return {
        row: index + 2,
        name,
        siret,
        vat_number: vatNumber,
        address,
        postal_code: postalCode,
        city,
        iban,
        bic,
        receipt_prefix: receiptPrefix,
        signature_name: signatureName,
        email_reply_to: emailReplyTo,
        errors,
        valid: errors.length === 0,
      };
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const parsed = parseCSV(text);
      setParsedData(parsed);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!selectedClientId) throw new Error('Veuillez sélectionner un client');

      const validSCIs = parsedData.filter(s => s.valid);
      if (validSCIs.length === 0) throw new Error('Aucune SCI valide à importer');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      const scisToInsert = validSCIs.map(s => ({
        organization_id: profile.organization_id,
        client_id: selectedClientId,
        name: s.name,
        siret: s.siret || null,
        vat_number: s.vat_number || null,
        address: s.address || null,
        postal_code: s.postal_code || null,
        city: s.city || null,
        iban: s.iban || null,
        bic: s.bic || null,
        receipt_prefix: s.receipt_prefix || null,
        signature_name: s.signature_name || null,
        email_reply_to: s.email_reply_to || null,
      }));

      const { error } = await supabase.from('scis').insert(scisToInsert);
      if (error) throw error;

      return validSCIs.length;
    },
    onSuccess: (count) => {
      toast.success(`${count} SCI(s) importée(s) avec succès`);
      queryClient.invalidateQueries({ queryKey: ['scis'] });
      resetDialog();
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const resetDialog = () => {
    setOpen(false);
    setParsedData([]);
    setFileName('');
    setSelectedClientId('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validCount = parsedData.filter(s => s.valid).length;
  const invalidCount = parsedData.filter(s => !s.valid).length;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetDialog();
      else setOpen(true);
    }}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          Importer (CSV)
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importer des SCIs
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Template Download */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Télécharger le template</p>
                <p className="text-sm text-muted-foreground">
                  Utilisez ce fichier CSV comme modèle pour vos données
                </p>
              </div>
              <Button variant="outline" onClick={downloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Template CSV
              </Button>
            </div>
          </div>

          {/* Client Selection */}
          <div className="space-y-2">
            <Label>Client *</Label>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Fichier CSV</Label>
            <div className="flex gap-2">
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="flex-1"
              />
            </div>
            {fileName && (
              <p className="text-sm text-muted-foreground">Fichier: {fileName}</p>
            )}
          </div>

          {/* Preview Table */}
          {parsedData.length > 0 && (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center gap-4 mb-2">
                <Badge variant="secondary" className="gap-1">
                  <Check className="h-3 w-3" /> {validCount} valide(s)
                </Badge>
                {invalidCount > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <X className="h-3 w-3" /> {invalidCount} erreur(s)
                  </Badge>
                )}
              </div>
              <ScrollArea className="flex-1 border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Ligne</TableHead>
                      <TableHead className="w-16">Statut</TableHead>
                      <TableHead>Nom</TableHead>
                      <TableHead>SIRET</TableHead>
                      <TableHead>Adresse</TableHead>
                      <TableHead>Ville</TableHead>
                      <TableHead>IBAN</TableHead>
                      <TableHead>Erreurs</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedData.map((sci, idx) => (
                      <TableRow key={idx} className={!sci.valid ? 'bg-destructive/10' : ''}>
                        <TableCell>{sci.row}</TableCell>
                        <TableCell>
                          {sci.valid ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-destructive" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{sci.name || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{sci.siret || '-'}</TableCell>
                        <TableCell>{sci.address || '-'}</TableCell>
                        <TableCell>{sci.city || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{sci.iban ? `...${sci.iban.slice(-4)}` : '-'}</TableCell>
                        <TableCell className="text-sm text-destructive">
                          {sci.errors.join(', ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={resetDialog}>
              Annuler
            </Button>
            <Button
              onClick={() => importMutation.mutate()}
              disabled={validCount === 0 || !selectedClientId || importMutation.isPending}
            >
              {importMutation.isPending ? 'Import...' : `Importer ${validCount} SCI(s)`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
